import { computed, effectScope, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import useDelete from '@/core/composables/api/useDelete'
import useGet from '@/core/composables/api/useGet'
import usePost from '@/core/composables/api/usePost'
import usePut from '@/core/composables/api/usePut'
import { ApiRequestStatus } from '@/core/models/api/api-request-status'
import { memberFullName } from '@/core/models/member/member-name'
import { useSnackStore } from '@/stores/snack.store'
import type {
  LessonDetail,
  StartLessonFeedbackBody,
  UpdateLessonFeedbackBody,
} from '../models/lesson'
import { lessonFeedbackUpdateError } from './lesson-feedback-update-error'
import { lessonStatusCode } from './lesson-status-code'

const createLessonApi = () => {
  const route = useRoute()
  const router = useRouter()
  const { t } = useI18n()
  const snackStore = useSnackStore()

  const lessonId = computed(() => {
    const id = route.params.lessonId
    return typeof id === 'string' ? id : undefined
  })
  const endpoint = computed(() => `lesson/${lessonId.value}`)
  const startFeedbackEndpoint = computed(() => `${endpoint.value}/start-feedback`)
  const changeFeedbackEndpoint = computed(() => `${endpoint.value}/feedback`)
  const endEndpoint = computed(() => `${endpoint.value}/end`)

  const { data: lesson, status, load, reload } = useGet<LessonDetail>(endpoint)
  const { mutate: renameRequest, status: renameStatus } = usePut<void, { title: string }>(endpoint)
  const {
    mutate: startFeedbackRequest,
    status: feedbackStatus,
    error: feedbackError,
  } = usePost<void, StartLessonFeedbackBody>(startFeedbackEndpoint)
  const {
    mutate: updateFeedbackRequest,
    status: updateFeedbackStatus,
    error: updateFeedbackError,
  } = usePut<void, UpdateLessonFeedbackBody>(changeFeedbackEndpoint)
  const { mutate: endRequest, status: endStatus, error: endError } = usePost<void>(endEndpoint)
  const { remove: deleteRequest, status: deleteStatus } = useDelete<void>(endpoint)

  watch(
    lessonId,
    (id) => {
      if (id) {
        load()
      }
    },
    { immediate: true },
  )

  const renameLesson = async (title: string): Promise<boolean> => {
    await renameRequest({ title: title.trim() })

    if (renameStatus.value === ApiRequestStatus.Success) {
      snackStore.showInfo(t('lumia.lesson.snackbar.renameSuccess'))
      await reload()
      return true
    }

    snackStore.showError(t('lumia.lesson.snackbar.renameError'))
    return false
  }

  const startFeedback = async (body: StartLessonFeedbackBody): Promise<boolean> => {
    await startFeedbackRequest(body)

    if (feedbackStatus.value === ApiRequestStatus.Success) {
      snackStore.showInfo(t('lumia.lesson.snackbar.feedbackStarted'))
      await reload()
      return true
    }

    if (lessonStatusCode(feedbackError.value) === 409) {
      await reload()
    }
    snackStore.showError(t('lumia.lesson.snackbar.feedbackError'))
    return false
  }

  const memberName = (memberId: string): string => {
    const current = lesson.value
    if (!current) {
      return memberId
    }
    const member = [
      ...current.teams.flatMap((team) => team.subjects),
      ...current.members,
      ...(current.feedback?.subjects ?? []),
    ].find((candidate) => candidate.id === memberId)
    return member ? memberFullName(member) : memberId
  }

  const updateFeedback = async (body: UpdateLessonFeedbackBody): Promise<boolean> => {
    await updateFeedbackRequest(body)

    if (updateFeedbackStatus.value === ApiRequestStatus.Success) {
      snackStore.showInfo(t('lumia.lesson.snackbar.feedbackUpdated'))
      await reload()
      return true
    }

    const described = lessonFeedbackUpdateError(updateFeedbackError.value, memberName)
    const code = lessonStatusCode(updateFeedbackError.value)
    if (code === 409 || code === 404) {
      await reload()
    }
    snackStore.showError(described.values ? t(described.key, described.values) : t(described.key))
    return false
  }

  const endLesson = async (): Promise<boolean> => {
    await endRequest()

    if (endStatus.value === ApiRequestStatus.Success) {
      snackStore.showInfo(t('lumia.lesson.snackbar.ended'))
      await reload()
      return true
    }

    if (lessonStatusCode(endError.value) === 409) {
      await reload()
    }
    snackStore.showError(t('lumia.lesson.snackbar.endError'))
    return false
  }

  const deleteLesson = async (): Promise<boolean> => {
    await deleteRequest()

    if (deleteStatus.value === ApiRequestStatus.Success) {
      snackStore.showInfo(t('lumia.lesson.snackbar.deleted'))
      await router.push({ name: 'impulse-library' })
      return true
    }

    snackStore.showError(t('lumia.lesson.snackbar.deleteError'))
    return false
  }

  return {
    lesson,
    status,
    reload,
    renameLesson,
    startFeedback,
    updateFeedback,
    endLesson,
    deleteLesson,
  }
}

let lessonApi: ReturnType<typeof createLessonApi> | undefined

/** One lesson request, shared by the page and the running session header. */
export const useLesson = () => {
  if (!lessonApi) {
    lessonApi = effectScope(true).run(() => createLessonApi())
  }

  if (!lessonApi) {
    throw new Error('useLesson failed to initialize')
  }

  return lessonApi
}
